import { useState, useEffect, useCallback } from "react";
import { getAllSalesByAgentId } from "../api/salesApi";
import { useUser } from "../context/userContext";
import { UseFetchGroupAgents } from "./useFetchAgent";

export function UseFetchAgentSales(shouldFetch = true) {
    const { user } = useUser();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(shouldFetch);
    const [error, setError] = useState(null);

    // memoized fetch — accepts an optional AbortSignal
    const fetchSales = useCallback(async (signal) => {
        if (!shouldFetch) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const response = await getAllSalesByAgentId(user.userId, { signal });
            const salesArray = Array.isArray(response)
                ? response
                : (
                    response && Array.isArray(response.data)
                        ? response.data
                        : []
                );
            setData(salesArray);
        }
        catch (err) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        }
        finally {
            setLoading(false);
        }
    }, [shouldFetch, user.userId]);

    // call once on mount, with an AbortController
    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        const ac = new AbortController();
        fetchSales(ac.signal);
        return () => ac.abort();
    }, [fetchSales, shouldFetch]);

    // refresh exposed to consumers
    const refresh = useCallback(() => {
        if (shouldFetch) {
            fetchSales();
        }
    }, [fetchSales, shouldFetch]);

    return { data, loading, error, refresh };
}

export function UseFetchDownlineSales(downlineAgentArray = [], shouldFetch = true) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(shouldFetch);
    const [error, setError] = useState(null);

    // fetch and accumulate sales for each downline agent
    const fetchDownlineSales = useCallback(async (signal) => {
        if (!shouldFetch) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            if (!Array.isArray(downlineAgentArray) || downlineAgentArray.length === 0) {
                setData([]);
                return;
            }

            const allSales = [];

            // sequential to respect abort signal and avoid hammering backend;
            // change to parallel with Promise.allSettled if desired.
            for (const agent of downlineAgentArray) {
                if (signal?.aborted) {
                    break;
                }
                const agentId = agent?.userId;
                if (!agentId) {
                    continue;
                }

                try {
                    const response = await getAllSalesByAgentId(agentId, { signal });
                    const salesArray = Array.isArray(response)
                        ? response
                        : (
                            response && Array.isArray(response.data)
                                ? response.data
                                : []
                        );

                    if (salesArray.length) {
                        // Inject agentId to ensure we can identify the sales agent
                        const salesWithAgentId = salesArray.map(s => ({ ...s, agentId }));
                        allSales.push(...salesWithAgentId);
                    }
                }
                catch (innerErr) {
                    // ignore individual agent errors unless it's AbortError
                    if (innerErr.name === "AbortError") throw innerErr;
                    console.error(`Failed to fetch sales for agent ${agentId}`, innerErr);
                }
            }
            setData(allSales);
        }
        catch (err) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        }
        finally {
            setLoading(false);
        }
    }, [downlineAgentArray, shouldFetch]);

    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        const ac = new AbortController();
        fetchDownlineSales(ac.signal);
        return () => ac.abort();
    }, [fetchDownlineSales, shouldFetch]);

    const refresh = useCallback(() => {
        if (shouldFetch) {
            fetchDownlineSales();
        }
    }, [fetchDownlineSales, shouldFetch]);

    return { data, loading, error, refresh };
}

export function UseFetchGroupSales(shouldFetch = true) {
    const { data: groupAgentArray = [], loading: loadingGroupAgents, error: groupAgentsError } = UseFetchGroupAgents(shouldFetch);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(shouldFetch);
    const [error, setError] = useState(null);

    const fetchGroupSales = useCallback(async (signal) => {
        if (!shouldFetch) {
            return;
        }
        setLoading(true);
        setError(null);

        try {
            if (!Array.isArray(groupAgentArray) || groupAgentArray.length === 0) {
                setData([]); // no agents -> empty result
                return;
            }

            const allSales = [];

            for (const agent of groupAgentArray) {
                if (signal?.aborted) {
                    break;
                }
                const response = await getAllSalesByAgentId(agent.userId, { signal });
                const salesArray = Array.isArray(response)
                    ? response
                    : (response && Array.isArray(response.data) ? response.data : []);

                if (salesArray.length > 0) {
                    const salesWithAgentId = salesArray.map(s => ({ ...s, agentId: agent.userId }));
                    allSales.push(...salesWithAgentId);
                }
            }

            setData(allSales);
        }
        catch (err) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        }
        finally {
            setLoading(false);
        }
    }, [groupAgentArray, shouldFetch]);

    // run whenever agentArray changes
    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }
        // Only fetch if we have agents or if we want to try (but logic above handles empty agents)
        // Wait for agents to load? 
        // If loadingGroupAgents is true, maybe we should wait? 
        // But useEffect depends on agentArray, so it will re-run when agents arrive.
        if (loadingGroupAgents) {
            return;
        }

        const ac = new AbortController();
        fetchGroupSales(ac.signal);
        return () => ac.abort();
    }, [fetchGroupSales, shouldFetch, loadingGroupAgents]);

    const refresh = useCallback(() => {
        if (shouldFetch) {
            fetchGroupSales();
        }
    }, [fetchGroupSales, shouldFetch]);

    return { data, loading: (shouldFetch ? (loading || loadingGroupAgents) : false), error: error || groupAgentsError, refresh };
}